import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";
import {
  type VidimoHapticKind,
  VidimoHaptics,
} from "@/lib/native/vidimo-haptics";
import { hapticsEnabled } from "@/lib/preferences/haptics";

/**
 * House rules for haptics in this app. Read these before adding a call site;
 * the whole value of a buzz is that it stays rare enough to be believed.
 *
 * 1. A haptic is NEVER the sole feedback. The system decides whether to play
 *    anything at all (iPad has no haptic hardware, the app must be foregrounded,
 *    the user's System Haptics setting is not readable from here) and iOS
 *    resolves the bridge call before the generator is even touched, so there is
 *    no delivery receipt. Every site needs a visible companion. If there is no
 *    visual, the bug is the missing visual.
 * 2. Success buzzes only when the host cannot see the result. No navigation, no
 *    full-screen change, no persistent visual at the instant it fires. If the
 *    screen changes, the screen is the feedback.
 * 3. Failure buzzes when the only channel is a transient toast. Never on
 *    client-side validation: nothing was attempted, so nothing was awaited.
 * 4. "Warning" means exactly one thing here: it completed, but it is not done.
 *    Not "destructive", not "are you sure". Emotional colouring is not a type.
 * 5. Impact and notification are different vocabularies and are not
 *    interchangeable. Impact is a physical metaphor (Apple defines every style
 *    in terms of collision and mass); notification is the outcome of a task.
 *    Picking one for how it feels is the specific misuse Apple calls out.
 * 6. `medium` means a gesture engaged. `light` means a discrete thing
 *    registered. One meaning each, forever. Never `medium` on a commit.
 * 7. No gesture, no haptic. Nothing on a timer, sync tick, incoming push,
 *    foreground resume, connectivity change, or another user's action.
 * 8. One buzz per gesture. Walk the whole call chain before adding a site, not
 *    just the file: dismiss-then-callback and void-ed async cross files.
 * 9. Repeated elements never buzz. List rows, checklist rows, keystrokes,
 *    scroll, gesture detents, tab bars, filter chips, day steppers.
 * 10. Silence is never a signal. Never teach "no buzz means it worked".
 * 11. Call these from the UI event handler, never from a collection mutator, a
 *     render body, a state updater, or after a fire-and-forget call.
 */

type HapticImpact = "light" | "medium" | "heavy";
type HapticOutcome = "success" | "warning" | "error";

const STYLE_BY_IMPACT: Record<HapticImpact, ImpactStyle> = {
  heavy: ImpactStyle.Heavy,
  light: ImpactStyle.Light,
  medium: ImpactStyle.Medium,
};

const TYPE_BY_OUTCOME: Record<HapticOutcome, NotificationType> = {
  error: NotificationType.Error,
  success: NotificationType.Success,
  warning: NotificationType.Warning,
};

/**
 * Collapses a burst into one buzz. Long enough to swallow a loop firing on
 * consecutive ticks (the PowerSync upload path walks a CRUD queue in a `while`),
 * short enough that it can never eat a second deliberate action — every
 * haptic-bearing action in this app costs the host at least a sheet or a scan.
 */
const BURST_WINDOW_MS = 250;
let lastFiredAt = 0;

/**
 * Run `fire` if a haptic is warranted right now, swallowing whatever it does.
 *
 * `kind` is what Android needs: its own plugin takes one flat vocabulary, so
 * the impact/notification split — an iOS distinction — is resolved here rather
 * than at each call site.
 */
function play(kind: VidimoHapticKind, fireOnIos: () => Promise<void>): void {
  // A hard no-op off native. `@capacitor/haptics` IS implemented on web, but
  // that implementation throws `UNAVAILABLE` wherever `navigator.vibrate` is
  // missing (all of desktop Safari and every iOS browser, which has never
  // supported the Vibration API), so routing through it would trade a buzz
  // nobody gets for a rejected promise every caller has to catch.
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  if (!hapticsEnabled()) {
    return;
  }
  const now = Date.now();
  if (now - lastFiredAt < BURST_WINDOW_MS) {
    return;
  }
  lastFiredAt = now;

  // Android goes to our own plugin, not `@capacitor/haptics`, whose Android
  // path is raw `Vibrator` waveforms: they reach the system as USAGE_UNKNOWN
  // (bucketed with media, so Android's "Touch feedback" setting cannot silence
  // them) and its patterns are wrong besides. See lib/native/vidimo-haptics.ts.
  const fire =
    Capacitor.getPlatform() === "android"
      ? () => VidimoHaptics.perform({ kind })
      : fireOnIos;

  fire().catch(() => {
    // ignore — haptics are non-essential and must never break the interaction
    // that triggered them.
  });
}

/**
 * A physical metaphor: something landed, snapped into place, or engaged. Not an
 * outcome — use `outcomeHaptic` for whether a task succeeded or failed.
 */
export function impactHaptic(weight: HapticImpact = "light"): void {
  play(weight, () => Haptics.impact({ style: STYLE_BY_IMPACT[weight] }));
}

/**
 * The outcome of a task the host asked for. Maps to iOS's
 * `UINotificationFeedbackGenerator`, whose three types mean exactly "completed",
 * "produced a warning" and "failed", and on Android to `CONFIRM` / `REJECT`.
 */
export function outcomeHaptic(outcome: HapticOutcome): void {
  play(outcome, () => Haptics.notification({ type: TYPE_BY_OUTCOME[outcome] }));
}

/** Clear the burst window between tests. Not used by app code. */
export function resetHapticThrottleForTest(): void {
  lastFiredAt = 0;
}
